import { useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Field = ({ testId, ...props }) => <input data-testid={testId} {...props} className="w-full bg-transparent border border-border rounded-xl px-4 py-3 outline-none focus:border-accent transition-colors" />;
const Primary = ({ testId, children, ...props }) => <button type="submit" data-testid={testId} {...props} className="w-full rounded-full bg-primary text-primary-foreground py-3.5 text-sm hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">{children}</button>;
const err = (e, fb) => toast.error(e.response?.data?.detail || fb);

const Setup = ({ onDone }) => {
  const [f, setF] = useState({ admin_key: "", password: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/access/organizer/setup", f); toast.success("Organizer account created — now sign in"); onDone(); }
    catch (ex) { err(ex, "Setup could not be completed"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="organizer-setup-form">
      <p className="text-xs text-muted-foreground">First-time setup: confirm with the current access key, then choose the Organizer password and a separate Security PIN.</p>
      <Field testId="organizer-setup-key" type="password" placeholder="Current access key" value={f.admin_key} onChange={(e) => setF({ ...f, admin_key: e.target.value })} autoComplete="off" />
      <Field testId="organizer-setup-password" type="password" placeholder="New Organizer password (8+ characters)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" />
      <Field testId="organizer-setup-pin" type="password" inputMode="numeric" placeholder="Security PIN (4–8 digits, different from password)" value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value })} autoComplete="off" />
      <Primary testId="organizer-setup-submit" disabled={busy || !f.admin_key || !f.password || !f.pin}>{busy ? "Creating…" : "Create Organizer account"}</Primary>
    </form>
  );
};

const Legacy = ({ onLegacy }) => {
  const [k, setK] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const res = await fetch(`${api.defaults.baseURL}/admin/settings`, { headers: { "X-Admin-Key": k, "X-PL-Client": "web" } });
      if (!res.ok) throw new Error(res.status === 401 ? "Access key not accepted (it may have been switched off)." : "Server error");
      onLegacy(k);
    } catch (ex) { toast.error(ex.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="organizer-legacy-form">
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">Emergency access with the legacy key. Set up / use the two-step Organizer sign-in whenever possible.</p>
      <Field testId="organizer-legacy-key" type="password" placeholder="Legacy access key" value={k} onChange={(e) => setK(e.target.value)} autoComplete="off" />
      <Primary testId="organizer-legacy-submit" disabled={busy || !k}>{busy ? "Checking…" : "Continue with legacy key"}</Primary>
    </form>
  );
};

/* Organizer Control Center sign-in: Step 1 password → Step 2 Security PIN (development two-step; Step 2 is replaceable by real MFA). */
export const OrganizerLogin = ({ status, onAuthed, onLegacy, emergency, onSetupDone }) => {
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const step1 = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/access/organizer/login/step1", { password }); setPassword(""); setStep(2); }
    catch (ex) { err(ex, "Sign-in details not recognised."); } finally { setBusy(false); }
  };
  const step2 = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/access/organizer/login/step2", { pin }); setPin(""); onAuthed(); }
    catch (ex) { err(ex, "Security PIN not recognised."); if (ex.response?.status === 401 && /start again/i.test(ex.response?.data?.detail || "")) setStep(1); } finally { setBusy(false); }
  };
  const showSetup = status && !status.configured;
  const showLegacy = emergency && status?.legacy_key_enabled !== false && !showSetup;
  return (
    <div className="min-h-screen min-h-dvh grid place-items-center bg-[#EDECE3] px-6 py-6" data-testid="organizer-login-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm bg-white/80 border border-white/60 rounded-3xl shadow-[0_8px_40px_rgba(26,46,38,0.08)] p-8">
        <div className="grid place-items-center w-12 h-12 rounded-full bg-primary text-primary-foreground mb-5">{step === 2 ? <KeyRound size={20} /> : <Lock size={20} />}</div>
        <h1 className="font-serif text-3xl mb-1" data-testid="organizer-login-title">Organizer Control Center</h1>
        <p className="text-sm text-muted-foreground mb-6">Authorized Organizer access only.</p>
        {showSetup ? <Setup onDone={onSetupDone} /> : showLegacy ? <Legacy onLegacy={onLegacy} /> : step === 1 ? (
          <form onSubmit={step1} className="space-y-4" data-testid="organizer-step1-form">
            <Field testId="organizer-password-input" type="password" placeholder="Organizer password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            <Primary testId="organizer-step1-submit" disabled={busy || !password}>{busy ? "Checking…" : "Sign In Securely"}</Primary>
          </form>
        ) : (
          <form onSubmit={step2} className="space-y-4" data-testid="organizer-step2-form">
            <p className="text-xs rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2">Step 1 verified. Step 2: enter your Organizer Security PIN.</p>
            <Field testId="organizer-pin-input" type="password" inputMode="numeric" placeholder="Security PIN" value={pin} onChange={(e) => setPin(e.target.value)} autoComplete="one-time-code" />
            <Primary testId="organizer-step2-submit" disabled={busy || !pin}>{busy ? "Verifying…" : "Sign In Securely"}</Primary>
            <button type="button" data-testid="organizer-step-back" onClick={() => setStep(1)} className="w-full text-xs text-muted-foreground underline underline-offset-2">Start again</button>
          </form>
        )}
        <p className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground"><ShieldCheck size={12} /> Protected access for the PositiveLife Organizer.</p>
        <a href="/guide" data-testid="organizer-guide-link" className="mt-3 block text-center text-[11px] text-muted-foreground/70 hover:text-foreground">Looking for the Guide Workspace?</a>
      </motion.div>
    </div>
  );
};
